import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/types";

const typeLabel: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  web: "网络剧",
};

export default function HeroCarousel({ items }: { items: Work[] }) {
  const [index, setIndex] = useState(0);
  const touchX = useRef<number | null>(null);
  const count = items.length;
  const current = items[index];

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), 6500);
    return () => clearInterval(timer);
  }, [count]);

  const go = (i: number) => setIndex(((i % count) + count) % count);

  if (!current) return null;

  return (
    <section className="relative w-full h-[520px] overflow-hidden bg-primary-950 md:h-[640px]">
      {items.map((work, i) => (
        <div
          key={work.id}
          className={`absolute inset-0 transition-opacity duration-1000 ease-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={work.backdrop}
            alt={`${work.title}剧照`}
            title={`${work.title}剧情介绍`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40"></div>

      <div
        className="relative z-10 mx-auto flex h-full w-full max-w-[1360px] flex-col justify-center px-4 md:px-6"
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const delta = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
          touchX.current = null;
        }}
      >
        <span className="mb-3 inline-flex w-fit items-center gap-2 rounded-full border border-accent-400/40 bg-accent-500/15 px-3 py-1 text-xs font-medium text-accent-200 backdrop-blur">
          <span className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-fire-line"></i>
          </span>
          妻子的朋友 · 本周精选
        </span>

        <h1 className="max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
          热门影视剧剧情介绍与观影推荐
        </h1>

        <div key={current.id} className="mt-5 max-w-xl animate-fade-up">
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            {current.title}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-primary-100">
            <span className="flex items-center gap-1 font-semibold text-accent-300">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-star-fill text-xs"></i>
              </span>
              {current.rating.toFixed(1)}
            </span>
            <span className="text-primary-400">|</span>
            <span>{typeLabel[current.type]}</span>
            <span className="text-primary-400">|</span>
            <span>
              {current.year} · {current.region}
            </span>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-primary-100 line-clamp-2 md:text-base">
            {current.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex h-11 items-center gap-2 rounded-md bg-accent-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-circle-line"></i>
              </span>
              查看剧情详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="flex h-11 items-center gap-2 rounded-md border border-primary-100/30 bg-primary-50/10 px-5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-primary-50/20 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-grid-line"></i>
              </span>
              浏览同类作品
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 right-4 hidden items-center gap-2 md:right-6 md:flex">
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-primary-100/30 bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-900/60 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-primary-100/30 bg-primary-950/40 text-background-50 backdrop-blur transition-colors hover:bg-primary-900/60 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        <div className="absolute bottom-8 left-4 flex items-center gap-2 md:left-6">
          {items.map((work, i) => (
            <button
              key={work.id}
              type="button"
              aria-label={`切换到${work.title}`}
              onClick={() => go(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-primary-100/40 hover:bg-primary-100/70"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}